import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ColumnDef, Table as TanstackTable } from "@tanstack/react-table";
import { Input } from "@/components/ui/base/input";
import { Button } from "@/components/ui/base/button";
import { Badge } from "@/components/ui/base/badge";
import { BodySection } from "@/components/ui/BodySection";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { DataTableFacetedFilter } from "@/components/ui/data-table/DataTableFacetedFilter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/base/popover";
import { Filter, Loader2, Search, X } from "lucide-react";
import { ROUTES, merchantPath } from "@/routes";
import { useStateMachine } from "@/core/arch/state_machine";
import { SupportRequestsRepository } from "@/application/support/support_repository";
import { SupportListBloc } from "@/application/support/support_list_bloc";
import type { SupportTicketRow } from "@/application/support/types";
import { topicLabel } from "@/application/support/types";
import { SupportStatusBadge } from "./components/SupportStatusBadge";
import { SupportDetailSheet } from "./components/SupportDetailSheet";

// ── Constants ────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { label: "Synced", value: "Synced" },
  { label: "Pending", value: "Pending" },
  { label: "Failed", value: "Failed" },
];

const TOPIC_OPTIONS = [
  { label: "Setup & verification", value: "setup-verification" },
  { label: "Account settings", value: "account-settings" },
  { label: "Payments", value: "payments" },
  { label: "Payouts", value: "payouts" },
  { label: "Troubleshooting", value: "troubleshooting" },
  { label: "Referral program", value: "referral-program" },
  { label: "Something else", value: "other" },
];

// ── Helpers ──────────────────────────────────────────────────────────

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

function arrFilterFn(
  row: { getValue: (id: string) => unknown },
  columnId: string,
  filterValue: string[],
): boolean {
  const value = row.getValue(columnId) as string | null;
  return filterValue.includes(value ?? "");
}

// ── Toolbar ─────────────────────────────────────────────────────────

function SupportToolbar({
  table,
  searchQuery,
  onSearchChange,
}: {
  table: TanstackTable<SupportTicketRow>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
}) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const activeFilterCount = table
    .getState()
    .columnFilters.reduce(
      (sum, f) => sum + (Array.isArray(f.value) ? f.value.length : 1),
      0,
    );

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <Search className="text-subtle-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Search by reference..."
          className="w-56 pl-9"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <Badge
                variant="secondary"
                className="rounded-sm px-1 text-[10px] font-normal"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="flex flex-col gap-2">
            {table.getColumn("status") && (
              <DataTableFacetedFilter
                column={table.getColumn("status")!}
                title="Status"
                options={STATUS_OPTIONS}
              />
            )}
            {table.getColumn("topic") && (
              <DataTableFacetedFilter
                column={table.getColumn("topic")!}
                title="Topic"
                options={TOPIC_OPTIONS}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>
      {isFiltered && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => table.resetColumnFilters()}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Reset filters</span>
        </Button>
      )}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────

export function SupportView() {
  const [state, bloc] = useStateMachine(
    () => new SupportListBloc(new SupportRequestsRepository()),
  );

  const [localSearch, setLocalSearch] = useState("");
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const businessId = searchParams.get("businessId") ?? "";
  const urlQuery = searchParams.get("q") ?? "";

  /**
   * The URL is the source of truth for the server-side filters, so a
   * narrowed view is a link someone can send. The merchant page links here
   * with `businessId` rather than duplicating the table.
   */
  useEffect(() => {
    void bloc.setFilter({ businessId, searchQuery: urlQuery });
  }, [bloc, businessId, urlQuery]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSearchParams(
          (params) => {
            if (value) params.set("q", value);
            else params.delete("q");
            return params;
          },
          { replace: true },
        );
      }, 300);
    },
    [setSearchParams],
  );

  const columns: ColumnDef<SupportTicketRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "reference",
        header: "Reference",
        cell: ({ row }) => (
          <span className="text-foreground font-mono text-sm font-medium">
            {row.getValue("reference")}
          </span>
        ),
      },
      {
        accessorKey: "topic",
        header: "Topic",
        cell: ({ row }) => (
          <span className="text-foreground text-sm">
            {topicLabel(row.getValue("topic"))}
          </span>
        ),
        filterFn: arrFilterFn,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <SupportStatusBadge status={row.original.status} />,
        filterFn: arrFilterFn,
      },
      {
        accessorKey: "businessId",
        header: "Merchant",
        cell: ({ row }) => {
          const businessId = row.getValue("businessId") as string | null;
          return businessId ? (
            <span className="text-muted-foreground font-mono text-xs">
              {businessId}
            </span>
          ) : (
            <span className="text-subtle-foreground">—</span>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {formatDate(row.getValue("createdAt"))}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <BodySection
        title="Support Tickets"
        action={
          businessId ? (
            <Link
              to={ROUTES.SUPPORT}
              className="border-border text-foreground hover:border-muted-foreground/40 hover:bg-muted flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium"
            >
              <X className="h-3 w-3" />
              Clear merchant filter
            </Link>
          ) : null
        }
      >
        {businessId && (
          <p className="text-muted-foreground mb-4 text-sm">
            Showing tickets for merchant{" "}
            <Link
              to={merchantPath(businessId)}
              className="font-mono text-xs text-blue-600 hover:underline"
            >
              {businessId}
            </Link>
          </p>
        )}

        {state.status === "loading" && state.items.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
          </div>
        ) : state.status === "error" ? (
          <div className="bg-card rounded-lg border px-4 py-8 text-center text-sm text-red-500">
            Failed to load support requests: {state.error}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={state.items}
            onRowClick={(row) => setOpenTicketId(row.id)}
            toolbar={(table) => (
              <SupportToolbar
                table={table}
                searchQuery={localSearch}
                onSearchChange={handleSearchChange}
              />
            )}
          />
        )}
      </BodySection>

      {openTicketId && (
        <SupportDetailSheet
          ticketId={openTicketId}
          onClose={() => setOpenTicketId(null)}
          onRetried={() => void bloc.load()}
        />
      )}
    </div>
  );
}
